import { ReactNode, useState, useCallback, useRef, CSSProperties } from "react";
import {
  DndContext,
  DragOverEvent,
  DragStartEvent,
  UniqueIdentifier,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  KeyboardSensor,
  CollisionDetection,
  Collision,
  MeasuringStrategy,
  closestCenter,
  pointerWithin,
  rectIntersection,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useDebounceCallback } from "@/hooks/useDebounceCallback";

// Debounce for handleDragOver itself. Coalesces dragOver events that fire
// faster than this; ~1 frame at 60fps is roughly the shortest interval
// that's ever meaningfully distinguishable from continuous pointer
// movement. Starting value after WEBV3-73 removed the per-droppable DOM
// reads that used to make a longer debounce necessary — confirm via
// manual mouse and touch testing (WEBV3-75) before relying on this value.
export const DRAG_OVER_DEBOUNCE_MS = 16;

// Two-phase collision detection: phase 1 resolves which container the
// drag is over using ONLY container-level droppables, filtered to those
// currently intersecting the viewport (the outer grid itself scrolls —
// see Dashboard's `overflow-auto` wrapper — so a scrolled-out container's
// rect can still be real but coincidentally overlap the on-screen position
// of a different container). Phase 2 then finds the closest item within
// that confirmed container to compute insertion position. Item rects
// belonging to other containers — including cards scrolled out of view in
// a large, non-virtualized list — never participate in deciding which
// container is targeted.
export const containerAwareCollisionDetection: CollisionDetection = (args) => {
  const { droppableRects, droppableContainers } = args;

  // No `window` outside the browser (SSR, non-DOM test environments) — skip
  // the viewport check rather than throwing, since collision detection only
  // ever runs client-side during an active drag anyway.
  const viewportHeight =
    typeof window === "undefined" ? Infinity : window.innerHeight;
  const viewportWidth =
    typeof window === "undefined" ? Infinity : window.innerWidth;

  const containerDroppables = droppableContainers.filter((container) => {
    if (container.data.current?.type !== "container") return false;

    const rect = droppableRects.get(container.id);
    if (!rect) return false;

    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < viewportHeight &&
      rect.left < viewportWidth
    );
  });

  let containerCollisions: Collision[] = pointerWithin({
    ...args,
    droppableContainers: containerDroppables,
  });

  if (containerCollisions.length === 0) {
    containerCollisions = rectIntersection({
      ...args,
      droppableContainers: containerDroppables,
    });
  }

  if (containerCollisions.length === 0) {
    containerCollisions = closestCenter({
      ...args,
      droppableContainers: containerDroppables,
    });
  }

  if (containerCollisions.length === 0) {
    return [];
  }

  const targetContainerId = containerCollisions[0].id;

  const itemDroppablesInContainer = droppableContainers.filter(
    (container) =>
      container.data.current?.type === "item" &&
      container.data.current?.containerId === targetContainerId
  );

  if (itemDroppablesInContainer.length === 0) {
    return containerCollisions;
  }

  return closestCenter({
    ...args,
    droppableContainers: itemDroppablesInContainer,
  });
};

interface MoveItemBetweenContainersArgs {
  containers: Record<UniqueIdentifier, UniqueIdentifier[]>;
  activeContainerId: UniqueIdentifier;
  overContainerId: UniqueIdentifier;
  activeId: UniqueIdentifier;
  overId: UniqueIdentifier;
  isOverContainer: boolean;
}

// Computes the containers update for moving an item from one container to
// another. Returns null (no-op) if `activeContainerId`/`overContainerId`
// aren't present in this `containers` snapshot, or if `activeId` isn't found
// in the source container — e.g. a stale snapshot from an out-of-date
// closure racing a container being removed — rather than spreading
// `undefined` or letting `indexOf` return -1 and silently deleting an
// unrelated item via `splice(-1, 1)`.
export function moveItemBetweenContainers({
  containers,
  activeContainerId,
  overContainerId,
  activeId,
  overId,
  isOverContainer,
}: MoveItemBetweenContainersArgs): Record<
  UniqueIdentifier,
  UniqueIdentifier[]
> | null {
  const activeContainerItems = containers[activeContainerId];
  const overContainerItems = containers[overContainerId];
  if (!activeContainerItems || !overContainerItems) {
    return null;
  }

  const activeItems = [...activeContainerItems];
  const overItems = [...overContainerItems];

  const activeIndex = activeItems.indexOf(activeId);
  if (activeIndex === -1) {
    return null;
  }
  activeItems.splice(activeIndex, 1);

  if (isOverContainer) {
    overItems.push(activeId);
  } else {
    const overIndex = overItems.indexOf(overId);
    if (overIndex >= 0) {
      overItems.splice(overIndex, 0, activeId);
    } else {
      overItems.push(activeId);
    }
  }

  return {
    ...containers,
    [activeContainerId]: [...new Set(activeItems)],
    [overContainerId]: [...new Set(overItems)],
  };
}

interface DragDropContextProps {
  children: ReactNode;
  // Must be referentially stable (e.g. a bare `useState` setter) across
  // renders — handleDragOver's identity stability (see the refs comment
  // below, WEBV3-79) depends on it never changing during a drag.
  onContainersUpdate?: (
    containers: Record<UniqueIdentifier, UniqueIdentifier[]>
  ) => void;
  containers: Record<UniqueIdentifier, UniqueIdentifier[]>;
  className?: string;
  style?: CSSProperties;
  renderDragOverlay?: (
    activeId: UniqueIdentifier,
    containerId: UniqueIdentifier | null
  ) => ReactNode;
  onDragEnd?: (itemId: UniqueIdentifier, containerId: UniqueIdentifier) => void;
}

export const DragDropContext = ({
  children,
  containers,
  onContainersUpdate,
  className,
  style,
  renderDragOverlay,
  onDragEnd,
}: DragDropContextProps) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeContainer, setActiveContainer] =
    useState<UniqueIdentifier | null>(null);

  // `containers` is read via a ref (not state) inside `handleDragOver` so
  // that callback's identity stays stable across a drag. Otherwise every
  // container-crossing would recreate the debounced wrapper below, orphaning
  // its pending trailing call with a stale `containers` closure — see
  // WEBV3-79.
  const containersRef = useRef(containers);
  containersRef.current = containers;

  // Configure sensors with appropriate activation constraints
  const sensors = useSensors(
    // useSensor(PointerSensor),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    })
  );

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id);

    // Find which container this item belongs to
    const containerId = active.data.current?.containerId;
    if (containerId) {
      setActiveContainer(containerId);
    }
  }, []);

  // Handle drag over for sorting and moving between containers
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;

      // Exit if there's nothing to hover over
      if (!over) return;

      const activeId = active.id;
      const overId = over.id;

      // Only handle item dragging (not container dragging)
      if (active.data.current?.type !== "item") return;

      // Find the containers involved
      const activeContainerId = active.data.current.containerId;

      // Determine if we're hovering over a container or an item
      const isOverContainer = over.data.current?.type === "container";
      const overContainerId = isOverContainer
        ? over.id
        : over.data.current?.containerId;

      // Exit if we can't determine the containers
      if (!activeContainerId || !overContainerId) return;

      // If hovering over a different container
      if (activeContainerId !== overContainerId) {
        const updatedContainers = moveItemBetweenContainers({
          containers: containersRef.current,
          activeContainerId,
          overContainerId,
          activeId,
          overId,
          isOverContainer,
        });

        if (!updatedContainers) return;

        onContainersUpdate?.(updatedContainers);

        // Update active container to reflect the move
        setActiveContainer(overContainerId);
      }
      // If hovering over the same container but different item (reordering)
      else if (!isOverContainer && activeId !== overId) {
        // Reorder within the same container. Guard against a stale
        // snapshot where `activeContainerId` no longer exists — e.g. the
        // container was removed while this debounced call was pending.
        const containerItems = containersRef.current[activeContainerId];
        if (!containerItems) return;

        const items = [...containerItems];
        const activeIndex = items.indexOf(activeId);
        const overIndex = items.indexOf(overId);

        if (activeIndex !== -1 && overIndex !== -1) {
          // Perform the array move
          const newItems = arrayMove(items, activeIndex, overIndex);

          // Update containers state
          onContainersUpdate?.({
            ...containersRef.current,
            [activeContainerId]: [...new Set(newItems)],
          });
        }
      }
    },
    [onContainersUpdate]
  );

  const debouncedHandleDragOver = useDebounceCallback(
    handleDragOver,
    DRAG_OVER_DEBOUNCE_MS
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (onDragEnd && activeId && activeContainer) {
      onDragEnd(activeId, activeContainer);
    }
    // Reset active states
    setActiveId(null);
    setActiveContainer(null);
  }, [activeId, activeContainer, onDragEnd]);

  const collisionDetectionStrategy2: CollisionDetection = useCallback(
    (args) => {
      return containerAwareCollisionDetection(args);
    },
    []
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy2}
      onDragStart={handleDragStart}
      onDragOver={debouncedHandleDragOver}
      onDragEnd={handleDragEnd}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.WhileDragging,
        },
      }}
    >
      <div className={className} style={style}>
        {children}
      </div>

      {/* Render a drag overlay if provided */}
      {renderDragOverlay && activeId && (
        <DragOverlay
          zIndex={9999}
          style={{
            cursor: "grabbing",
          }}
          dropAnimation={{
            duration: 200,
            easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
          }}
        >
          {renderDragOverlay(activeId, activeContainer)}
        </DragOverlay>
      )}
    </DndContext>
  );
};
